/**
 * Custom Refine notification provider backed by Sonner toasts (frontend.spec
 * §20). Maps Refine's `OpenNotificationParams` onto Sonner:
 *   - `type: 'success'`  → `toast.success`
 *   - `type: 'error'`    → `toast.error`
 *   - `type: 'progress'` → `toast.loading` (undoable mutations show a countdown)
 *
 * When a `key` is provided it is reused as the Sonner toast id so Refine's
 * `close(key)` (e.g. after an undoable mutation resolves) dismisses the exact
 * toast. UI toasts are for in-portal user actions only; system notifications go
 * to Teams from the backend.
 */

import type { NotificationProvider, OpenNotificationParams } from '@refinedev/core';
import { toast } from 'sonner';

/** Translate Refine's `undoableTimeout` (seconds) into Sonner's ms duration. */
function toDuration(params: OpenNotificationParams): number | undefined {
  if (params.type === 'progress' && typeof params.undoableTimeout === 'number') {
    return params.undoableTimeout * 1000;
  }
  return undefined;
}

export const notificationProvider: NotificationProvider = {
  open: (params: OpenNotificationParams): void => {
    const { key, message, description, type, cancelMutation } = params;
    const data = {
      id: key,
      description,
      duration: toDuration(params),
    };

    switch (type) {
      case 'success':
        toast.success(message, data);
        break;
      case 'error':
        toast.error(message, data);
        break;
      case 'progress':
        // Undoable mutation in-flight: offer an "Undo" action that cancels it.
        toast.loading(message, {
          ...data,
          action: cancelMutation
            ? { label: 'Undo', onClick: () => cancelMutation() }
            : undefined,
        });
        break;
      default:
        toast(message, data);
        break;
    }
  },

  close: (key: string): void => {
    toast.dismiss(key);
  },
};

export default notificationProvider;
